import type { DashboardFilters, FilterData, Resource } from "./types";
import { CardError, CardSkeleton } from "./CardState";
import { formatTime } from "../lib/datetime";

// A tab per district reads well for a handful; past this, the row would run
// off the card, so a dropdown takes over.
const MAX_DISTRICT_TABS = 5;

type Props = {
  filters: DashboardFilters;
  onChange: (filters: DashboardFilters) => void;
  options: Resource<FilterData>;
  updatedAt: Date;
};

export function FilterBar({ filters, onChange, options, updatedAt }: Props) {
  if (options.loading) return <div className="rounded-2xl border border-zinc-200 bg-white p-4"><CardSkeleton rows={2} /></div>;
  if (options.error) return <div className="rounded-2xl border border-zinc-200 bg-white p-4"><CardError message={options.error} retry={options.retry} /></div>;
  const data = options.data;
  if (!data) return null;
  const centres = data.centres.filter((centre) => !filters.district || centre.districtId === filters.district);
  return <div className="flex flex-wrap items-center gap-3 rounded-2xl border border-zinc-200 bg-white p-2.5">
    {data.districts.length <= MAX_DISTRICT_TABS ? <div className="flex max-w-full overflow-x-auto rounded-xl bg-zinc-100 p-1" aria-label="District filter">
      {[{ id: "", name: "All districts" }, ...data.districts].map((district) => <button
        type="button" key={district.id || "all"}
        onClick={() => onChange({ ...filters, district: district.id, centre: "" })}
        className={`shrink-0 rounded-lg px-3 py-2 text-sm font-medium transition ${filters.district === district.id ? "bg-white text-zinc-900 shadow-sm" : "text-zinc-500 hover:text-zinc-900"}`}
      >{district.name}</button>)}
    </div> : <label className="flex min-w-0 items-center gap-2 rounded-xl border border-zinc-200 px-3 text-sm text-zinc-500">
      District
      <select value={filters.district} onChange={(event) => onChange({ ...filters, district: event.target.value, centre: "" })} className="h-10 max-w-52 bg-transparent font-medium text-zinc-900 outline-none">
        <option value="">All districts</option>{data.districts.map((district) => <option key={district.id} value={district.id}>{district.name}</option>)}
      </select>
    </label>}
    <label className="flex items-center gap-2 rounded-xl border border-zinc-200 px-3 text-sm text-zinc-500">
      Centre
      <select value={filters.centre} onChange={(event) => onChange({ ...filters, centre: event.target.value })} className="h-10 bg-transparent font-medium text-zinc-900 outline-none">
        <option value="">All centres</option>{centres.map((centre) => <option key={centre.id} value={centre.id}>{centre.name}</option>)}
      </select>
    </label>
    <label className="flex items-center gap-2 rounded-xl border border-zinc-200 px-3 text-sm text-zinc-500">
      Module
      <select value={filters.module} onChange={(event) => onChange({ ...filters, module: event.target.value })} className="h-10 max-w-52 bg-transparent font-medium text-zinc-900 outline-none">
        <option value="">All modules</option>{data.modules.map((module) => <option key={module.id} value={module.id}>{module.name}</option>)}
      </select>
    </label>
    <time className="ml-auto px-2 font-mono text-xs text-zinc-500" dateTime={updatedAt.toISOString()}>Updated {formatTime(updatedAt.toISOString())} PKT</time>
  </div>;
}
